/**
 * STON.fi API Service - TON DEX
 *
 * STON.fi is the main DEX on TON blockchain.
 * Used for TON ↔ TON swaps.
 *
 * API endpoint, router, and token addresses switch via networkConfig.
 */

import { getStonfiConfig } from './networkConfig';

function getApiBase(): string {
  return getStonfiConfig().apiBase;
}

// Dynamic token lookup reflecting current network
export const TON_TOKENS: Record<string, string> = new Proxy({} as Record<string, string>, {
  get(_target, prop: string) {
    return getStonfiConfig().tokens[prop];
  },
  ownKeys() {
    return Object.keys(getStonfiConfig().tokens);
  },
  getOwnPropertyDescriptor(_target, prop: string) {
    const tokens = getStonfiConfig().tokens;
    if (prop in tokens) {
      return { configurable: true, enumerable: true, value: tokens[prop] };
    }
    return undefined;
  },
  has(_target, prop: string) {
    return prop in getStonfiConfig().tokens;
  },
});

export interface StonfiPoolInfo {
  address: string;
  reserve0: string;
  reserve1: string;
  token0_address: string;
  token1_address: string;
  lp_fee: string;
  protocol_fee: string;
  ref_fee: string;
  collected_token0_protocol_fee: string;
  collected_token1_protocol_fee: string;
}

export interface StonfiSwapSimulation {
  offer_address: string;
  ask_address: string;
  offer_units: string;
  ask_units: string;
  slippage_tolerance: string;
  min_ask_units: string;
  swap_rate: string;
  price_impact: string;
  fee_address: string;
  fee_units: string;
  fee_percent: string;
  router_address: string;
  pool_address: string;
}

export interface StonfiQuoteRequest {
  offerAddress: string;  // Token to sell (jetton master address)
  askAddress: string;    // Token to buy (jetton master address)
  offerUnits: string;    // Amount in smallest units
  slippageTolerance: string; // e.g., "0.01" for 1%
}

export interface StonfiSwapParams {
  userWalletAddress: string;
  offerAddress: string;
  askAddress: string;
  offerUnits: string;
  minAskUnits: string;
  referralAddress?: string;
}

/**
 * Get TON token address from symbol (reads from current network config)
 */
export function getTonTokenAddress(symbol: string, contractAddress?: string): string | null {
  if (contractAddress) {
    return contractAddress;
  }
  return getStonfiConfig().tokens[symbol] || null;
}

class StonfiApiService {
  /**
   * Get swap simulation (quote) from STON.fi
   */
  async getSwapSimulation(request: StonfiQuoteRequest): Promise<StonfiSwapSimulation> {
    // STON.fi API requires query params with POST method
    const params = new URLSearchParams({
      offer_address: request.offerAddress,
      ask_address: request.askAddress,
      units: request.offerUnits,
      slippage_tolerance: request.slippageTolerance,
    });

    const response = await fetch(`${getApiBase()}/v1/swap/simulate?${params}`, {
      method: 'POST',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to get STON.fi quote: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get pool info
   */
  async getPoolInfo(poolAddress: string): Promise<StonfiPoolInfo> {
    const response = await fetch(`${getApiBase()}/v1/pools/${poolAddress}`);

    if (!response.ok) {
      throw new Error(`Failed to get pool info: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get all available pools
   */
  async getPools(): Promise<StonfiPoolInfo[]> {
    const response = await fetch(`${getApiBase()}/v1/pools`);

    if (!response.ok) {
      throw new Error(`Failed to get pools: ${response.statusText}`);
    }

    const data = await response.json();
    return data.pool_list || [];
  }

  /**
   * Get assets (tokens) list
   */
  async getAssets(): Promise<Array<{
    contract_address: string;
    symbol: string;
    display_name: string;
    decimals: number;
    image_url?: string;
  }>> {
    const response = await fetch(`${getApiBase()}/v1/assets`);

    if (!response.ok) {
      throw new Error(`Failed to get assets: ${response.statusText}`);
    }

    const data = await response.json();
    return data.asset_list || [];
  }

  /**
   * Get quote with simplified parameters
   */
  async getSimpleQuote(
    fromSymbol: string,
    toSymbol: string,
    amount: string,
    slippagePercent: number = 1,
    fromContract?: string,
    toContract?: string
  ): Promise<StonfiSwapSimulation> {
    const offerAddress = getTonTokenAddress(fromSymbol, fromContract);
    const askAddress = getTonTokenAddress(toSymbol, toContract);

    if (!offerAddress) {
      throw new Error(`Unknown TON token: ${fromSymbol}`);
    }
    if (!askAddress) {
      throw new Error(`Unknown TON token: ${toSymbol}`);
    }

    return this.getSwapSimulation({
      offerAddress,
      askAddress,
      offerUnits: amount,
      slippageTolerance: (slippagePercent / 100).toString(),
    });
  }

  /**
   * Build swap message for TON wallet
   * Returns the message body that needs to be sent to execute the swap
   */
  buildSwapMessage(params: StonfiSwapParams): {
    to: string;
    value: string;
    payload: string;
  } {
    // STON.fi router address (dynamic per network)
    const ROUTER_ADDRESS = getStonfiConfig().routerAddress;

    // This is a simplified version - actual implementation needs proper TL-B encoding
    // In practice, you'd use @ston-fi/sdk for proper message building
    return {
      to: ROUTER_ADDRESS,
      value: '300000000', // 0.3 TON for gas
      payload: JSON.stringify({
        op: 'swap',
        offer_address: params.offerAddress,
        ask_address: params.askAddress,
        offer_units: params.offerUnits,
        min_ask_units: params.minAskUnits,
        referral: params.referralAddress,
      }),
    };
  }
}

export const stonfiApi = new StonfiApiService();
export { StonfiApiService };
